import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Stack,
  LinearProgress,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LaunchIcon from '@mui/icons-material/Launch';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';

import {
  getPendingAiCards,
  confirmAiCard,
  confirmAllAiCards,
  saveAiCards,
  CARD_CATEGORIES,
} from '../api/aiCardsApi';
import { useToast } from '../context/ToastContext';
import { dismissAiReview } from '../services/fcmNotificationService';

import VitalsAiCard from '../components/AiCards/VitalsAiCard';
import ConditionsAiCard from '../components/AiCards/ConditionsAiCard';
import RisksAiCard from '../components/AiCards/RisksAiCard';
import ObservationAiCard from '../components/AiCards/ObservationAiCard';
import PrescriptionsAiCard from '../components/AiCards/PrescriptionsAiCard';
import GenericAiCard from '../components/AiCards/GenericAiCard';
import DynamicAiCard from '../components/AiCards/DynamicAiCard';

const AiCardsReview = () => {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const showToast = useToast();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [patientId, setPatientId] = useState(null);
  const [cards, setCards] = useState([]);
  const [cardFormValues, setCardFormValues] = useState({});
  const [confirmedCardTypes, setConfirmedCardTypes] = useState(new Set());
  const [confirmingCardType, setConfirmingCardType] = useState(null);
  const [isConfirmingAll, setIsConfirmingAll] = useState(false);

  // Track field changes across dynamic cards
  const handleFormChange = useCallback((cardType, formData) => {
    setCardFormValues((prev) => ({
      ...prev,
      [cardType]: formData,
    }));
  }, []);

  // Fetch pending AI cards
  const loadCards = useCallback(async (isRefresh = false) => {
    if (!consultationId) return;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const res = await getPendingAiCards(consultationId);
      if (res.success) {
        const fetchedCards = res.cards || [];
        setCards(fetchedCards);
        if (res.patientId) setPatientId(res.patientId);

        // Prepopulate form values for schema cards
        const initialMap = {};
        fetchedCards.forEach((c) => {
          if (Array.isArray(c.fields)) {
            const rowData = {};
            c.fields.forEach((f) => {
              if (f.value !== undefined) rowData[f.key] = f.value;
            });
            initialMap[c.cardType] = rowData;
          }
        });
        setCardFormValues(initialMap);
      } else {
        setError(res.message || 'Failed to load AI consultation cards');
      }
    } catch (err) {
      setError(err?.message || 'Error loading AI cards');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [consultationId]);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  // Handle individual card confirm
  const handleConfirmCard = async (cardType, data) => {
    setConfirmingCardType(cardType);
    try {
      const res = await confirmAiCard(consultationId, cardType, data);
      if (res.success) {
        setConfirmedCardTypes((prev) => new Set([...prev, cardType]));
        showToast(
          res.alreadyConfirmed
            ? `${cardType} was already confirmed`
            : `${cardType} confirmed and saved!`,
          'success'
        );

        // Check if all cards will now be confirmed
        const totalCards = cards.length;
        const newConfirmedCount = confirmedCardTypes.size + 1;
        if (newConfirmedCount >= totalCards) {
          dismissAiReview(consultationId);
        }
      } else {
        showToast(res.message || `Failed to confirm ${cardType}`, 'error');
      }
    } catch (err) {
      showToast(err?.message || `Error confirming ${cardType}`, 'error');
    } finally {
      setConfirmingCardType(null);
    }
  };

  // Handle draft save for an individual card
  const handleSaveDraft = async (cardType, data) => {
    try {
      const results = await saveAiCards(consultationId, [{ cardType, data }]);
      const res = results.find((r) => r.cardType === cardType) || results[0];
      if (res?.success) {
        showToast(
          res.alreadyConfirmed
            ? `${cardType} is already confirmed`
            : `${cardType} draft saved!`,
          'success'
        );
        setCards((prev) =>
          prev.map((c) =>
            c.cardType === cardType
              ? { ...c, isEditedByDoctor: true, draftSavedAt: new Date().toISOString() }
              : c
          )
        );
      } else {
        showToast(res?.message || `Failed to save draft for ${cardType}`, 'error');
      }
    } catch (err) {
      showToast(err?.message || `Error saving draft`, 'error');
    }
  };

  // Handle confirm all pending cards
  const handleConfirmAll = async () => {
    const unconfirmedCards = cards.filter((c) => !confirmedCardTypes.has(c.cardType));
    if (unconfirmedCards.length === 0) return;

    setIsConfirmingAll(true);
    try {
      // Use latest edited form data for each card
      const cardsToConfirm = unconfirmedCards.map((c) => ({
        ...c,
        data: cardFormValues[c.cardType] || c.data || c,
      }));

      const results = await confirmAllAiCards(consultationId, cardsToConfirm);
      let successCount = 0;
      const newlyConfirmed = new Set(confirmedCardTypes);

      results.forEach((res) => {
        if (res.success) {
          successCount++;
          newlyConfirmed.add(res.cardType);
        }
      });

      setConfirmedCardTypes(newlyConfirmed);

      if (successCount === unconfirmedCards.length) {
        showToast(`All ${successCount} cards confirmed and persisted!`, 'success');
        dismissAiReview(consultationId);
      } else {
        showToast(`Confirmed ${successCount} of ${unconfirmedCards.length} cards`, 'info');
      }
    } catch (err) {
      showToast(err?.message || 'Error during batch confirmation', 'error');
    } finally {
      setIsConfirmingAll(false);
    }
  };

  // Navigate to consultation
  const handleOpenConsultation = () => {
    if (consultationId && patientId) {
      navigate(`/consultation/${consultationId}/patient/${patientId}/A/history`);
    } else {
      navigate('/dashboard');
    }
  };

  // Determine card component to render
  const renderCard = (card) => {
    const isConfirmed = confirmedCardTypes.has(card.cardType);
    const isConfirming = confirmingCardType === card.cardType || isConfirmingAll;

    const props = {
      card,
      onConfirm: handleConfirmCard,
      isConfirmed,
      isConfirming,
      onFormChange: handleFormChange,
      onSaveDraft: handleSaveDraft,
    };

    // 1. If card has fields schema, use schema-driven DynamicAiCard
    if (Array.isArray(card.fields) && card.fields.length > 0) {
      return <DynamicAiCard key={card.cardType} {...props} />;
    }

    // 2. Fallback for legacy format (cards with .data instead of .fields)
    if (CARD_CATEGORIES.VITALS.includes(card.cardType)) {
      return <VitalsAiCard key={card.cardType} {...props} />;
    }
    if (card.cardType === 'conditions') {
      return <ConditionsAiCard key={card.cardType} {...props} />;
    }
    if (card.cardType === 'risks') {
      return <RisksAiCard key={card.cardType} {...props} />;
    }
    if (card.cardType === 'observation') {
      return <ObservationAiCard key={card.cardType} {...props} />;
    }
    if (CARD_CATEGORIES.PRESCRIPTIONS.includes(card.cardType)) {
      return <PrescriptionsAiCard key={card.cardType} {...props} />;
    }
    return <GenericAiCard key={card.cardType} {...props} />;
  };

  // Calculate progress
  const totalCards = cards.length;
  const confirmedCount = cards.filter((c) => confirmedCardTypes.has(c.cardType)).length;
  const progressPercent = totalCards > 0 ? (confirmedCount / totalCards) * 100 : 100;
  const allConfirmed = totalCards > 0 && confirmedCount === totalCards;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC', pb: 8 }}>
      {/* Top App Header / Bar */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid #E2E8F0',
          bgcolor: '#FFFFFF',
          px: { xs: 2, md: 4 },
          py: 2,
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-btn"
              sx={{ color: '#64748B', textTransform: 'none' }}
            >
              Dashboard
            </Button>
            <Divider orientation="vertical" flexItem sx={{ height: 24, alignSelf: 'center' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  bgcolor: '#F3E8FF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#8A4BFF',
                }}
              >
                <AutoAwesomeIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: 16, md: 18 }, lineHeight: 1.2 }}>
                  Consultation Cards Review
                </Typography>
              </Box>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => loadCards(true)}
              disabled={refreshing || loading}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>

            {!allConfirmed && totalCards > 0 && (
              <Button
                variant="contained"
                size="small"
                startIcon={isConfirmingAll ? <CircularProgress size={16} color="inherit" /> : <DoneAllIcon />}
                onClick={handleConfirmAll}
                disabled={isConfirmingAll || totalCards === 0}
                data-testid="confirm-all-cards-btn"
                sx={{
                  bgcolor: '#F56423',
                  color: '#FFF',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  // '&:hover': { bgcolor: '#7434F5' },
                }}
              >
                {isConfirmingAll ? 'Saving All...' : 'Confirm All Pending'}
              </Button>
            )}

            {/* <Button
              variant="contained"
              size="small"
              color="primary"
              endIcon={<LaunchIcon />}
              onClick={handleOpenConsultation}
              data-testid="open-consultation-btn"
              sx={{
                bgcolor: '#0F172A',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                '&:hover': { bgcolor: '#1E293B' },
              }}
            >
              Open Consultation
            </Button> */}
          </Stack>
        </Stack>

        {/* Progress Tracker */}
        {totalCards > 0 && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
                Review Progress: {confirmedCount} of {totalCards} cards confirmed
              </Typography>
              <Chip
                label={`${Math.round(progressPercent)}% Complete`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  bgcolor: allConfirmed ? '#ECFDF5' : '#EDE9FE',
                  color: allConfirmed ? '#059669' : '#6D28D9',
                }}
              />
            </Stack>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: '#E2E8F0',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  background: allConfirmed
                    ? '#10B981'
                    : 'linear-gradient(90deg, #8A4BFF 0%, #3B82F6 100%)',
                },
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Main Content Area */}
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#8A4BFF', mb: 2 }} />
            <Typography variant="body1" sx={{ color: '#64748B' }}>
              Loading AI-extracted consultation cards...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => loadCards()}>
                Try Again
              </Button>
            }
            sx={{ mb: 3, borderRadius: 2 }}
          >
            {error}
          </Alert>
        )}

        {/* Caught-up state (no pending cards) */}
        {!loading && !error && cards.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              borderRadius: 4,
              border: '1px dashed #CBD5E1',
              bgcolor: '#FFFFFF',
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
            data-testid="no-pending-cards"
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#ECFDF5',
                color: '#059669',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 36 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0F172A', mb: 1 }}>
              All Caught Up!
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748B', maxWidth: 460, mb: 3 }}>
              No cards are pending review for this consultation. All data have been
              confirmed and committed to patient clinical records.
            </Typography>
            <Button
              variant="contained"
              onClick={()=>{
                navigate('/dashboard');
              }}
              sx={{
                bgcolor: '#8A4BFF',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                '&:hover': { bgcolor: '#7434F5' },
              }}
            >
              Continue to DashBoard
            </Button>
          </Paper>
        )}

        {/* All confirmed banner */}
        {allConfirmed && totalCards > 0 && (
          <Alert
            severity="success"
            icon={<CheckCircleIcon fontSize="inherit" />}
            action={
              <Button color="inherit" size="small" onClick={()=>{
                navigate('/dashboard');
              }}>
               Back to Dashboard
              </Button>
            }
            sx={{ mb: 3, borderRadius: 3, fontWeight: 500 }}
            data-testid="all-confirmed-alert"
          >
            All data have been confirmed.
          </Alert>
        )}

        {/* Card List */}
        {!loading && cards.length > 0 && (
          <Stack spacing={3}>
            {cards.map((card) => renderCard(card))}
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default AiCardsReview;
